import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { getLscLiveTruthStatus, LscApiError, runLscLiveTruthCheck } from '../api';
import BroadcasterSideConfirmationPanel from './BroadcasterSideConfirmationPanel';
import { getLscEntitlementErrorGuidance } from '../entitlementBillingErrors';
import { displaySeasonLabel } from '../seasonDisplay';
import type {
  LscLiveTruthLatestCheck,
  LscLiveTruthLeagueContext,
  LscLiveTruthPromotion,
  LscLiveTruthStatusResponse,
  LscLiveTruthStatusStream,
} from '../types';

export type LiveTruthStatusCardProps = {
  leagueId: string;
};

export type LiveTruthRowMessage = {
  kind: 'success' | 'error';
  text: string;
};

export type LiveTruthStatusCardViewProps = {
  status: LscLiveTruthStatusResponse | null;
  loading: boolean;
  error: string | null;
  leagueId?: string;
  onRefresh: () => void;
  onRunCheck?: (stream: LscLiveTruthStatusStream) => void;
  checkingStreamKeys?: ReadonlySet<string>;
  rowMessages?: ReadonlyMap<string, LiveTruthRowMessage>;
};

type StatusBadge = {
  label: string;
  className: string;
  testId: string;
};

export const FORBIDDEN_RENDER_KEYS = new Set([
  'outcome',
  'result',
  'artifact_refs',
  'provider_raw',
  'idempotency_key',
  'raw_event_summary',
]);

export function liveTruthStreamRowKey(stream: LscLiveTruthStatusStream): string {
  return `${stream.twitch_user_id ?? 'unknown'}:${stream.stream_id ?? ''}`;
}

export function isLiveTruthManualCheckCooldownActive(
  stream: LscLiveTruthStatusStream,
  leagueContext?: LscLiveTruthLeagueContext | null,
  nowMs: number = Date.now()
): boolean {
  const cooldownSeconds = leagueContext?.manual_check_cooldown_seconds ?? 120;
  const lastAt = stream.latest_live_truth?.sampled_at ?? stream.latest_live_truth?.created_at;
  if (!lastAt) return false;
  const parsed = Date.parse(lastAt);
  if (Number.isNaN(parsed)) return false;
  return nowMs - parsed < cooldownSeconds * 1000;
}

export function isLiveTruthManualCheckDisabled(
  stream: LscLiveTruthStatusStream,
  leagueContext?: LscLiveTruthLeagueContext | null,
  options?: { isChecking?: boolean; nowMs?: number }
): boolean {
  return getLiveTruthManualCheckDisabledReason(stream, leagueContext, options) != null;
}

export function getLiveTruthManualCheckDisabledReason(
  stream: LscLiveTruthStatusStream,
  leagueContext?: LscLiveTruthLeagueContext | null,
  options?: { isChecking?: boolean; nowMs?: number }
): string | null {
  if (leagueContext?.manual_checks_enabled === false) {
    return 'manual_checks_disabled';
  }
  if (leagueContext?.source_state_stale) {
    return 'source_state_stale';
  }
  if (stream.stream_state_stale) {
    return 'stream_state_stale';
  }
  if (!stream.is_live) {
    return 'not_live';
  }
  if (!stream.candidate) {
    return 'no_candidate';
  }
  if (!stream.twitch_user_id?.trim()) {
    return 'no_twitch_user_id';
  }
  if ((stream.candidate.alternate_candidates_count ?? 0) > 0) {
    return 'ambiguous_candidates';
  }
  if (options?.isChecking) {
    return 'checking';
  }
  if (isLiveTruthManualCheckCooldownActive(stream, leagueContext, options?.nowMs)) {
    return 'cooldown_active';
  }
  return null;
}

export function buildLiveTruthManualCheckPayload(
  stream: LscLiveTruthStatusStream
): {
  twitch_user_id: string;
  competition_match_id?: string;
  stream_id?: string;
} {
  const payload: {
    twitch_user_id: string;
    competition_match_id?: string;
    stream_id?: string;
  } = {
    twitch_user_id: String(stream.twitch_user_id ?? '').trim(),
  };
  const competitionMatchId = stream.candidate?.competition_match_id?.trim();
  if (competitionMatchId) {
    payload.competition_match_id = competitionMatchId;
  }
  const streamId = stream.stream_id?.trim();
  if (streamId) {
    payload.stream_id = streamId;
  }
  return payload;
}

export function mapLiveTruthManualCheckError(error: unknown): string {
  if (error instanceof LscApiError) {
    const entitlement = getLscEntitlementErrorGuidance(error);
    if (entitlement) {
      return entitlement.message;
    }
    switch (error.code) {
      case 'feature_disabled':
        return 'Live game detection checks are not enabled.';
      case 'check_cooldown_active':
        return 'A live game detection check was run recently. Please wait before trying again.';
      case 'not_live':
        return 'This stream is no longer live.';
      case 'unauthorized':
      case 'forbidden':
        return 'You do not have permission to run this check.';
      default:
        break;
    }
    if (error.status === 401 || error.status === 403) {
      return 'You do not have permission to run this check.';
    }
  }
  return 'Live game detection check could not be started.';
}

export function formatLiveTruthCheckSuccessMessage(
  check: LscLiveTruthLatestCheck | null | undefined
): string {
  const badge = getLiveTruthStatusBadge(check);
  return `Check complete: ${badge.label}`;
}

export function resolveLiveTruthCheckStatus(
  check: LscLiveTruthLatestCheck | null | undefined
): string {
  if (!check) return '';
  return String(
    check.status ?? check.verification_status ?? check.live_truth_status ?? ''
  )
    .trim()
    .toLowerCase();
}

export function streamNeedsBroadcasterSideConfirmation(
  stream: LscLiveTruthStatusStream
): boolean {
  const check = stream.latest_live_truth;
  if (check?.commissioner_action === 'confirm_broadcaster_side') {
    return true;
  }
  const status = resolveLiveTruthCheckStatus(check);
  return status === 'needs_side_review' || status === 'side_uncertain';
}

export const LIVE_TRUTH_MANUAL_RETRY_COPY = {
  rejectedMenu:
    'Stream appears to be in menu/franchise hub. This is not a permanent rejection. Retry after gameplay or a play-call screen is visible.',
  streamStale:
    'Stream state is stale. Click Sync Live Detection, then refresh this card before retrying live game detection.',
  noCheck:
    'No live game detection check recorded yet. Run a check once gameplay or a play-call screen is visible.',
  checkStale:
    'The last live game detection check expired. Run a fresh check while gameplay is visible.',
  cooldown:
    'Live game detection is cooling down. Try again shortly after gameplay is visible.',
} as const;

export function formatLiveTruthRetryCustomerCopy(
  retry: LscLiveTruthStatusStream['retry']
): string | null {
  if (!retry?.state) {
    return null;
  }
  const state = String(retry.state).trim().toLowerCase();
  if (state === 'watching' || state === 'not_started') {
    return 'Live check in progress. LSC will retry automatically when eligible.';
  }
  if (state === 'exhausted') {
    return 'Automatic live checks stopped after repeated rejections. Review Needs Attention or run a manual check when gameplay is visible.';
  }
  if (state === 'stopped') {
    return 'Automatic live checks paused. Run a manual check when gameplay is visible or review Needs Attention.';
  }
  if (retry.next_retry_at) {
    return 'Retrying live game detection automatically.';
  }
  return null;
}

export function isMenuRejectionStream(stream: LscLiveTruthStatusStream): boolean {
  const status = resolveLiveTruthCheckStatus(stream.latest_live_truth);
  if (status === 'rejected_menu' || status === 'rejected_non_gameplay') {
    return true;
  }
  const blockReason = stream.promotion?.block_reason?.trim().toLowerCase();
  return blockReason === 'menu_or_franchise_hub';
}

export function getLiveTruthCooldownRemainingSeconds(
  stream: LscLiveTruthStatusStream,
  leagueContext?: LscLiveTruthLeagueContext | null,
  nowMs: number = Date.now()
): number | null {
  const cooldownSeconds = leagueContext?.manual_check_cooldown_seconds ?? 120;
  const lastAt = stream.latest_live_truth?.sampled_at ?? stream.latest_live_truth?.created_at;
  if (!lastAt) return null;
  const parsed = Date.parse(lastAt);
  if (Number.isNaN(parsed)) return null;
  const elapsedSec = (nowMs - parsed) / 1000;
  const remaining = Math.ceil(cooldownSeconds - elapsedSec);
  return remaining > 0 ? remaining : null;
}

export function formatLiveTruthCooldownGuidance(
  stream: LscLiveTruthStatusStream,
  leagueContext?: LscLiveTruthLeagueContext | null,
  nowMs?: number
): string {
  const remaining = getLiveTruthCooldownRemainingSeconds(stream, leagueContext, nowMs);
  if (remaining == null) {
    return LIVE_TRUTH_MANUAL_RETRY_COPY.cooldown;
  }
  return `${LIVE_TRUTH_MANUAL_RETRY_COPY.cooldown} (~${remaining}s remaining)`;
}

export function getLiveTruthStatusBadge(
  check: LscLiveTruthLatestCheck | null | undefined
): StatusBadge {
  const normalized = resolveLiveTruthCheckStatus(check);
  if (!normalized) {
    return {
      label: 'Pending',
      className:
        'rounded-full border border-secondary-600 bg-secondary-800/60 px-2 py-0.5 text-xs text-secondary-300',
      testId: 'live-truth-status-pending-label',
    };
  }
  if (normalized === 'verified_live_game' || normalized === 'verified') {
    return {
      label: 'Verified Live',
      className:
        'rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-200',
      testId: 'live-truth-status-verified-label',
    };
  }
  if (normalized === 'rejected_menu') {
    return {
      label: 'Rejected: Menu',
      className:
        'rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200',
      testId: 'live-truth-status-rejected-menu-label',
    };
  }
  if (normalized === 'rejected_non_gameplay') {
    return {
      label: 'Rejected: No Gameplay',
      className:
        'rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200',
      testId: 'live-truth-status-rejected-gameplay-label',
    };
  }
  if (normalized === 'needs_schedule_sync') {
    return {
      label: 'Needs Schedule Sync',
      className:
        'rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-200',
      testId: 'live-truth-status-needs-sync-label',
    };
  }
  if (normalized === 'verification_failed' || normalized === 'provider_error') {
    return {
      label: 'Provider Error',
      className:
        'rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-xs text-red-200',
      testId: 'live-truth-status-provider-error-label',
    };
  }
  return {
    label: 'Review Needed',
    className:
      'rounded-full border border-secondary-600 bg-secondary-800/60 px-2 py-0.5 text-xs text-secondary-300',
    testId: 'live-truth-status-review-needed-label',
  };
}

export function formatLiveTruthConfidence(confidence: number | null | undefined): string | null {
  if (confidence == null || Number.isNaN(Number(confidence))) return null;
  const numeric = Number(confidence);
  const pct = numeric <= 1 ? Math.round(numeric * 100) : Math.round(numeric);
  return `${pct}% confidence`;
}

const BLOCK_REASON_FALLBACK_LABELS: Record<string, string> = {
  no_live_truth_check: 'No live game detection check yet',
  match_unresolved: 'Match not resolved yet',
  menu_or_franchise_hub: 'Stream appears to be in a menu or league hub',
  stream_offline: 'Stream is offline',
  no_active_stream: 'No active stream detected',
  visual_evidence_missing: 'Gameplay evidence not available yet',
  confidence_below_threshold: 'Detection confidence is below the publishing threshold',
};

export function formatBlockReasonLabel(
  reason: string,
  promotion?: LscLiveTruthPromotion | null
): string {
  if (promotion?.public_blocked_reasons?.length) {
    const index = (promotion.blocked_reasons ?? []).indexOf(reason);
    if (index >= 0 && promotion.public_blocked_reasons[index]) {
      return promotion.public_blocked_reasons[index];
    }
  }
  if (promotion?.public_block_reason && promotion.block_reason === reason) {
    return promotion.public_block_reason;
  }
  const normalized = reason.trim().toLowerCase();
  if (BLOCK_REASON_FALLBACK_LABELS[normalized]) {
    return BLOCK_REASON_FALLBACK_LABELS[normalized];
  }
  return reason.replace(/_/g, ' ');
}

function formatBroadcasterLabel(stream: LscLiveTruthStatusStream): string {
  const display = stream.twitch_display_name?.trim();
  if (display) return display;
  const login = stream.twitch_login?.trim();
  if (login) return login.startsWith('@') ? login : `@${login}`;
  return 'Broadcaster';
}

function formatMatchupLabel(stream: LscLiveTruthStatusStream): string | null {
  const candidate = stream.candidate;
  if (!candidate) return null;
  const home = candidate.home_team?.trim() || 'Home';
  const away = candidate.away_team?.trim() || 'Away';
  return `${home} vs ${away}`;
}

function formatStageSeason(candidate: LscLiveTruthStatusStream['candidate']): string | null {
  if (!candidate) return null;
  const parts = [
    candidate.stage_label?.trim(),
    displaySeasonLabel(candidate.season_label),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatTimestamp(value: string | null | undefined): string | null {
  const text = value?.trim();
  if (!text) return null;
  const parsed = Date.parse(text);
  if (Number.isNaN(parsed)) return text;
  return new Date(parsed).toLocaleString();
}

function formatTeamsDetected(teams: Record<string, string | null> | null | undefined): string | null {
  if (!teams) return null;
  const home = teams.home?.trim();
  const away = teams.away?.trim();
  if (home && away) return `${home} vs ${away}`;
  const entries = Object.entries(teams)
    .map(([key, value]) => (value?.trim() ? `${key}: ${value.trim()}` : null))
    .filter(Boolean);
  return entries.length > 0 ? entries.join(', ') : null;
}

function LiveTruthStatusBadge({ check }: { check: LscLiveTruthLatestCheck | null | undefined }) {
  const badge = getLiveTruthStatusBadge(check);
  return (
    <span className={badge.className} data-testid={badge.testId}>
      {badge.label}
    </span>
  );
}

function PromotionDetails({ promotion }: { promotion: LscLiveTruthPromotion | null | undefined }) {
  if (!promotion) return null;
  const blockedReasons = (promotion.blocked_reasons ?? []).filter(Boolean);
  return (
    <div className="space-y-1" data-testid="live-truth-promotion-details">
      <p className="text-xs text-secondary-400">
        Promotion eligible:{' '}
        <span className="text-secondary-200">{promotion.eligible ? 'Yes' : 'No'}</span>
        {' · '}
        Would appear in verified-only Live Games:{' '}
        <span className="text-secondary-200">
          {promotion.would_appear_in_verified_only_live_games ? 'Yes' : 'No'}
        </span>
      </p>
      {blockedReasons.length > 0 ? (
        <ul className="text-xs text-secondary-500 list-disc list-inside">
          {blockedReasons.map((reason) => (
            <li key={reason} data-testid={`live-truth-block-reason-${reason}`}>
              {formatBlockReasonLabel(reason, promotion)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type LiveTruthStreamRowProps = {
  stream: LscLiveTruthStatusStream;
  leagueContext?: LscLiveTruthLeagueContext | null;
  leagueId?: string;
  onRunCheck?: (stream: LscLiveTruthStatusStream) => void;
  onSideConfirmed?: () => void | Promise<void>;
  isChecking?: boolean;
  rowMessage?: LiveTruthRowMessage | null;
};

function LiveTruthStreamRow({
  stream,
  leagueContext,
  leagueId,
  onRunCheck,
  onSideConfirmed,
  isChecking = false,
  rowMessage = null,
}: LiveTruthStreamRowProps) {
  const check = stream.latest_live_truth;
  const confidenceLabel = formatLiveTruthConfidence(check?.confidence);
  const matchup = formatMatchupLabel(stream);
  const stageSeason = formatStageSeason(stream.candidate);
  const teamsDetected = formatTeamsDetected(check?.teams_detected ?? undefined);
  const lastChecked = formatTimestamp(check?.sampled_at ?? check?.created_at);
  const streamTiming =
    formatTimestamp(stream.last_event_at) ?? formatTimestamp(stream.stream_started_at);
  const rowKey = liveTruthStreamRowKey(stream);
  const disabledReason = getLiveTruthManualCheckDisabledReason(stream, leagueContext, {
    isChecking,
  });
  const runDisabled = disabledReason != null;
  const showMenuRetryGuidance = isMenuRejectionStream(stream);
  const showStreamStaleGuidance = disabledReason === 'stream_state_stale';
  const showCooldownGuidance = disabledReason === 'cooldown_active';
  const showSideConfirmation = streamNeedsBroadcasterSideConfirmation(stream);
  const retryCopy = formatLiveTruthRetryCustomerCopy(stream.retry);

  return (
    <div
      className="rounded-lg border border-secondary-700 bg-secondary-800/40 p-4 space-y-3"
      data-testid={`live-truth-stream-row-${stream.twitch_user_id ?? 'unknown'}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-white">{formatBroadcasterLabel(stream)}</span>
        <span className="text-xs text-emerald-300/90">{stream.is_live ? 'Live' : 'Offline'}</span>
        {stream.stream_state_stale ? (
          <span className="text-xs text-amber-300/90">Stream state stale</span>
        ) : null}
        <LiveTruthStatusBadge check={check} />
      </div>

      {streamTiming ? (
        <p className="text-xs text-secondary-500">Last event: {streamTiming}</p>
      ) : null}

      {retryCopy ? (
        <p className="text-xs text-amber-200/90" data-testid="live-truth-retry-status">
          {retryCopy}
        </p>
      ) : null}

      {stream.candidate ? (
        <div className="space-y-1" data-testid="live-truth-candidate-details">
          <p className="text-sm text-secondary-300">
            Candidate: <span className="text-white">{matchup}</span>
            {stageSeason ? ` · ${stageSeason}` : ''}
          </p>
          {stream.candidate.broadcaster_side ? (
            <p className="text-xs text-secondary-500">
              Broadcaster side: {formatBlockReasonLabel(stream.candidate.broadcaster_side)}
            </p>
          ) : null}
          {stream.candidate.match_confidence ? (
            <p className="text-xs text-secondary-500">
              Match confidence: {formatBlockReasonLabel(stream.candidate.match_confidence)}
            </p>
          ) : null}
          {(stream.candidate.alternate_candidates_count ?? 0) > 0 ? (
            <p className="text-xs text-secondary-500">
              Alternate candidates: {stream.candidate.alternate_candidates_count}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-secondary-500">No schedule candidate matched.</p>
      )}

      {check ? (
        <div className="space-y-1" data-testid="live-truth-check-details">
          {confidenceLabel ? (
            <p className="text-xs text-secondary-400">{confidenceLabel}</p>
          ) : null}
          {lastChecked ? (
            <p className="text-xs text-secondary-500">Last checked: {lastChecked}</p>
          ) : null}
          {check.check_stale ? (
            <p
              className="text-xs text-amber-300/90"
              data-testid="live-truth-retry-hint-check-stale"
            >
              {LIVE_TRUTH_MANUAL_RETRY_COPY.checkStale}
            </p>
          ) : null}
          {check.evidence_summary ? (
            <p className="text-xs text-secondary-500">{check.evidence_summary}</p>
          ) : null}
          {check.rejection_reason ? (
            <p className="text-xs text-amber-200/90">{check.rejection_reason}</p>
          ) : null}
          {check.scene_type ? (
            <p className="text-xs text-secondary-500">
              Scene: {formatBlockReasonLabel(check.scene_type)}
            </p>
          ) : null}
          <p className="text-xs text-secondary-500">
            Gameplay detected: {check.gameplay_detected ? 'Yes' : 'No'}
            {' · '}
            Scoreboard detected: {check.scoreboard_detected ? 'Yes' : 'No'}
          </p>
          {teamsDetected ? (
            <p className="text-xs text-secondary-500">Teams detected: {teamsDetected}</p>
          ) : null}
        </div>
      ) : (
        <p
          className="text-xs text-secondary-500"
          data-testid="live-truth-retry-hint-no-check"
        >
          {LIVE_TRUTH_MANUAL_RETRY_COPY.noCheck}
        </p>
      )}

      {showMenuRetryGuidance ? (
        <p
          className="text-xs text-amber-200/90"
          data-testid="live-truth-retry-hint-rejected-menu"
        >
          {LIVE_TRUTH_MANUAL_RETRY_COPY.rejectedMenu}
        </p>
      ) : null}

      <PromotionDetails promotion={stream.promotion} />

      {showSideConfirmation && leagueId ? (
        <BroadcasterSideConfirmationPanel
          leagueId={leagueId}
          homeTeamLabel={stream.candidate?.home_team}
          awayTeamLabel={stream.candidate?.away_team}
          twitchUserId={stream.twitch_user_id}
          streamId={stream.stream_id}
          competitionMatchId={stream.candidate?.competition_match_id}
          onConfirmed={onSideConfirmed}
          testIdPrefix={`live-truth-side-confirmation-${stream.twitch_user_id ?? 'unknown'}`}
        />
      ) : null}

      <div className="space-y-2 pt-1">
        {showStreamStaleGuidance ? (
          <p
            className="text-xs text-amber-300/90"
            data-testid="live-truth-retry-hint-stream-stale"
          >
            {LIVE_TRUTH_MANUAL_RETRY_COPY.streamStale}
          </p>
        ) : null}
        {showCooldownGuidance ? (
          <p
            className="text-xs text-amber-300/90"
            data-testid="live-truth-retry-hint-cooldown"
          >
            {formatLiveTruthCooldownGuidance(stream, leagueContext)}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => onRunCheck?.(stream)}
          disabled={runDisabled}
          className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`live-truth-run-check-${stream.twitch_user_id ?? 'unknown'}`}
          data-disabled-reason={disabledReason ?? undefined}
          aria-label="Run live game detection check"
        >
          {isChecking ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden data-testid="live-truth-run-check-loading" />
          ) : null}
          Run live game detection check
        </button>

        {rowMessage ? (
          <p
            className={`text-xs ${rowMessage.kind === 'success' ? 'text-emerald-200' : 'text-red-300'}`}
            data-testid={`live-truth-row-message-${rowKey}`}
          >
            {rowMessage.text}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function LiveTruthStatusCardView({
  status,
  loading,
  error,
  leagueId,
  onRefresh,
  onRunCheck,
  checkingStreamKeys,
  rowMessages,
}: LiveTruthStatusCardViewProps) {
  const streams = status?.streams ?? [];
  const leagueContext = status?.league_context;
  const manualChecksDisabled = leagueContext?.manual_checks_enabled === false;

  return (
    <div className="card space-y-4" data-testid="live-truth-status-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Live game detection</h2>
          <p className="text-sm text-secondary-400 mt-1">
            See how live streams are being evaluated before they can appear as verified Live League
            Games.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="btn-secondary inline-flex items-center gap-2 text-sm shrink-0"
          aria-label="Refresh live game detection status"
          data-testid="live-truth-status-refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="live-truth-status-loading">
          Loading live game detection status...
        </p>
      ) : error ? (
        <p className="text-sm text-red-300" data-testid="live-truth-status-error">
          Live game detection status is unavailable right now.
        </p>
      ) : (
        <>
          {manualChecksDisabled ? (
            <p className="text-xs text-secondary-400" data-testid="live-truth-manual-checks-disabled">
              Live game detection checks are disabled. Contact support if checks should be enabled.
            </p>
          ) : null}

          {leagueContext?.source_state_stale ? (
            <p className="text-xs text-amber-300/90" data-testid="live-truth-league-stale">
              League source state is stale. Verified promotion is blocked until schedule sync is
              current.
            </p>
          ) : null}

          {streams.length === 0 ? (
            <div className="space-y-1" data-testid="live-truth-status-empty">
              <p className="text-sm text-secondary-400">No live streams to evaluate right now.</p>
              <p className="text-xs text-secondary-500">
                When a league member goes live, live game detection status will appear here after a stream is
                matched or checked.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="live-truth-status-list">
              {streams.map((stream) => {
                const rowKey = liveTruthStreamRowKey(stream);
                return (
                  <LiveTruthStreamRow
                    key={rowKey}
                    stream={stream}
                    leagueContext={leagueContext}
                    leagueId={leagueId}
                    onRunCheck={onRunCheck}
                    onSideConfirmed={onRefresh}
                    isChecking={checkingStreamKeys?.has(rowKey) ?? false}
                    rowMessage={rowMessages?.get(rowKey) ?? null}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LiveTruthStatusCard: React.FC<LiveTruthStatusCardProps> = ({ leagueId }) => {
  const [status, setStatus] = useState<LscLiveTruthStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingStreamKeys, setCheckingStreamKeys] = useState<Set<string>>(new Set());
  const [rowMessages, setRowMessages] = useState<Map<string, LiveTruthRowMessage>>(new Map());

  const loadStatus = useCallback(
    async (options?: { showLoading?: boolean }) => {
      if (options?.showLoading !== false) {
        setLoading(true);
      }
      setError(null);
      try {
        const next = await getLscLiveTruthStatus(leagueId);
        setStatus(next);
      } catch {
        setError('unavailable');
      } finally {
        if (options?.showLoading !== false) {
          setLoading(false);
        }
      }
    },
    [leagueId]
  );

  const handleRunCheck = useCallback(
    async (stream: LscLiveTruthStatusStream) => {
      const rowKey = liveTruthStreamRowKey(stream);
      setCheckingStreamKeys((prev) => new Set(prev).add(rowKey));
      setRowMessages((prev) => {
        const next = new Map(prev);
        next.delete(rowKey);
        return next;
      });

      try {
        const result = await runLscLiveTruthCheck(leagueId, buildLiveTruthManualCheckPayload(stream));
        setRowMessages((prev) =>
          new Map(prev).set(rowKey, {
            kind: 'success',
            text: formatLiveTruthCheckSuccessMessage(result.check),
          })
        );
        await loadStatus({ showLoading: false });
      } catch (err) {
        setRowMessages((prev) =>
          new Map(prev).set(rowKey, {
            kind: 'error',
            text: mapLiveTruthManualCheckError(err),
          })
        );
      } finally {
        setCheckingStreamKeys((prev) => {
          const next = new Set(prev);
          next.delete(rowKey);
          return next;
        });
      }
    },
    [leagueId, loadStatus]
  );

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  return (
    <LiveTruthStatusCardView
      status={status}
      loading={loading}
      error={error}
      leagueId={leagueId}
      onRefresh={() => {
        void loadStatus();
      }}
      onRunCheck={(stream) => {
        void handleRunCheck(stream);
      }}
      checkingStreamKeys={checkingStreamKeys}
      rowMessages={rowMessages}
    />
  );
};

export default LiveTruthStatusCard;
